// src/ui/auth/CountryPickerPage.jsx

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { countries } from '../../utils/countries';

const SEARCH_BAR_HEIGHT = 60;

export const CountryPickerPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredCountries, setFilteredCountries] = useState(countries);
  const [searchBarProgress, setSearchBarProgress] = useState(0);

  const scrollRef = useRef(null);

  // Where to land after picking a country (mirrors the `await Navigator
  // .push<Country>` caller in Flutter). Defaults to /phone-auth since
  // that's the only screen using this picker today.
  const returnTo = location.state?.returnTo || '/phone-auth';

  // ── Filter countries ──────────────────────────────────────────────────────

  const applyFilter = useCallback((query) => {
    const q = query.trim().toLowerCase();
    if (!q) {
      setFilteredCountries(countries);
      return;
    }
    const filtered = countries.filter((c) =>
      c.name.toLowerCase().includes(q) ||
      c.isoCode.toLowerCase().includes(q) ||
      c.dialCode.includes(q)
    );
    setFilteredCountries(filtered);
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    applyFilter(value);
  };

  // ── Scroll handler for search bar animation (mobile only) ────────────────

  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return;
    const offset = scrollRef.current.scrollTop;
    const progress = Math.min(offset / SEARCH_BAR_HEIGHT, 1);
    setSearchBarProgress(progress);
  }, []);

  // ── Select country ────────────────────────────────────────────────────────

  const handleSelectCountry = (country) => {
    // NOTE: navigate(-1, { state }) does NOT work in React Router — state
    // can only be attached when navigating to an explicit path. We navigate
    // to the return path directly (replace: true so the picker doesn't
    // stay in history), carrying the selected country in state — this is
    // the web equivalent of Flutter's `Navigator.pop(context, c)`.
    navigate(returnTo, {
      state: { selectedCountry: country },
      replace: true,
    });
  };

  // ── Go back ───────────────────────────────────────────────────────────────

  const handleBack = () => {
    navigate(-1);
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  const headerHeight = 56 + 60 + 8; // top bar + search bar + gap

  const EmptyState = ({ compact }) => (
    <div className={compact ? 'flex items-center justify-center h-64' : 'flex items-center justify-center h-96'}>
      <div className="flex flex-col items-center gap-3">
        <svg className="w-13 h-13 text-white/15" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        <span className="text-white/35 text-sm font-medium">No countries found</span>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-filatom-bg">
      {/* ══════════════════════════════════════════════════════════════════════
          MOBILE LAYOUT (unchanged aside from the search bar fix below)
          — visible below the md breakpoint
         ══════════════════════════════════════════════════════════════════════ */}
      <div className="md:hidden">
        {/* ── Scrollable List ────────────────────────────────────────────── */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="h-screen overflow-y-auto"
          style={{ paddingTop: headerHeight, paddingBottom: 32 }}
        >
          {filteredCountries.length === 0 ? (
            <EmptyState compact />
          ) : (
            <div className="px-4">
              {filteredCountries.map((country, index) => (
                <div key={country.isoCode}>
                  <button
                    onClick={() => handleSelectCountry(country)}
                    className="w-full flex items-center justify-between py-3 hover:bg-filatom-card-hover transition-colors rounded-lg px-2"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{country.flag}</span>
                      <span className="text-white text-sm">{country.name}</span>
                    </div>
                    <span className="text-filatom-primary text-sm font-semibold">
                      +{country.dialCode}
                    </span>
                  </button>
                  {index < filteredCountries.length - 1 && (
                    <div className="h-px bg-white/10 mx-2" />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Gradient Overlay ───────────────────────────────────────────── */}
        <div
          className="absolute top-0 left-0 right-0 pointer-events-none"
          style={{
            height: headerHeight - SEARCH_BAR_HEIGHT * searchBarProgress,
            background: `linear-gradient(to bottom, rgba(8,9,11,0.95) 0%, rgba(8,9,11,0.75) 60%, rgba(8,9,11,0) 100%)`,
          }}
        />

        {/* ── Floating Header ────────────────────────────────────────────── */}
        <div className="absolute top-0 left-0 right-0">
          {/* Top Bar */}
          <div className="flex items-center justify-between px-4 h-14">
            <button
              onClick={handleBack}
              className="w-9 h-9 rounded-full flex items-center justify-center"
              style={{ backgroundColor: '#0F1013' }}
            >
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="text-white text-lg font-bold">Choose a country</span>
            <div className="w-9" /> {/* Spacer for balance */}
          </div>

          {/* Search Bar - slides up with scroll */}
          <div
            className="px-4 py-2 transition-all duration-200"
            style={{
              transform: `translateY(-${searchBarProgress * SEARCH_BAR_HEIGHT}px)`,
              opacity: 1 - searchBarProgress,
            }}
          >
            <div
              className="relative h-11 rounded-full overflow-hidden flex items-center"
              style={{ backgroundColor: '#23262C' }}
            >
              <svg
                width="16"
                height="16"
                className="text-gray-400 flex-shrink-0 ml-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search"
                className="w-full h-full bg-transparent text-white text-sm outline-none pl-2 pr-3 placeholder-gray-400"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          DESKTOP LAYOUT (new) — visible at md breakpoint and up
          Static header (no scroll-hide animation), centered content column,
          card grid instead of a single-column list.
         ══════════════════════════════════════════════════════════════════════ */}
      <div className="hidden md:block">
        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="sticky top-0 z-10 bg-filatom-bg/90 backdrop-blur-sm border-b border-white/10">
          <div className="max-w-5xl mx-auto px-8 h-20 flex items-center gap-5">
            <button
              onClick={handleBack}
              className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors"
              style={{ backgroundColor: '#0F1013' }}
              aria-label="Go back"
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <div className="flex-1">
              <h1 className="text-white text-2xl font-bold leading-tight">Choose a country</h1>
              <p className="text-white/40 text-sm mt-0.5">Select your country calling code</p>
            </div>

            {/* Search */}
            <div className="relative w-72 flex-shrink-0">
              <svg
                width="16"
                height="16"
                className="text-white/35 absolute left-3.5 top-1/2 -translate-y-1/2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search"
                className="w-full h-11 rounded-full bg-filatom-card border border-white/10 text-white text-sm placeholder-white/35 outline-none pl-10 pr-4 focus:border-filatom-primary/60 transition-colors"
              />
            </div>
          </div>
        </div>

        {/* ── Content ─────────────────────────────────────────────────────── */}
        <div className="max-w-5xl mx-auto px-8 py-10">
          {filteredCountries.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredCountries.map((country) => (
                <button
                  key={country.isoCode}
                  onClick={() => handleSelectCountry(country)}
                  className="flex items-center gap-4 p-5 rounded-2xl bg-filatom-card border border-white/10 hover:border-filatom-primary/50 hover:bg-white/[0.04] transition-all text-left"
                >
                  <span className="text-3xl flex-shrink-0">{country.flag}</span>

                  <div className="flex-1 min-w-0">
                    <div className="text-white text-base font-semibold truncate">{country.name}</div>
                  </div>

                  <span className="text-filatom-primary text-sm font-semibold flex-shrink-0">
                    +{country.dialCode}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CountryPickerPage;